import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { membershipFeesAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { OutstandingPlayer } from '../../types/membershipFee';
import PlayerPicker from '../players/PlayerPicker';

interface RemindFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** A tartozók (a kintlévőség listából). */
  debtors: OutstandingPlayer[];
  onSent: () => void;
}

type Scope = 'all' | 'ageGroups' | 'players';

const formatAmount = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;

/**
 * Fizetési emlékeztető most: minden tartozónak, egyes korosztályok vagy
 * kiválasztott játékosok szüleinek - emailben és/vagy push értesítésben.
 */
const RemindFeesModal: React.FC<RemindFeesModalProps> = ({ isOpen, onClose, debtors, onSent }) => {
  const { showToast } = useToast();
  const [scope, setScope] = useState<Scope>('all');
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [playerIds, setPlayerIds] = useState<number[]>([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendPush, setSendPush] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setScope('all');
    setAgeGroups([]);
    setPlayerIds([]);
    setSendEmail(true);
    setSendPush(true);
  }, [isOpen]);

  const ageGroupOptions = useMemo(
    () => Array.from(new Set(debtors.map((d) => d.ageGroup).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'hu')),
    [debtors]
  );

  const targets = useMemo(() => {
    if (scope === 'ageGroups') return debtors.filter((d) => ageGroups.includes(d.ageGroup));
    if (scope === 'players') return debtors.filter((d) => playerIds.includes(d.playerId));
    return debtors;
  }, [scope, debtors, ageGroups, playerIds]);

  const total = targets.reduce((sum, d) => sum + d.outstandingAmount, 0);
  const canSend = targets.length > 0 && (sendEmail || sendPush) && !sending;

  const send = async () => {
    setSending(true);
    try {
      const result = await membershipFeesAPI.sendReminders({
        // Mindenkinek: üres lista; egyébként a kiválasztott tartozók
        playerIds: scope === 'all' ? undefined : targets.map((t) => t.playerId),
        sendEmail,
        sendPush,
      });
      const missing =
        result.playersWithoutRecipient > 0
          ? ` ${result.playersWithoutRecipient} játékosnak nincs szülői fiókja, nekik nem ment.`
          : '';
      showToast(
        result.recipientCount > 0
          ? `Emlékeztető elküldve ${result.recipientCount} címzettnek.${missing}`
          : `Nem ment ki emlékeztető.${missing}`,
        result.recipientCount > 0 ? 'success' : 'error'
      );
      onSent();
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, 'Az emlékeztetőt nem sikerült elküldeni.'), 'error');
    } finally {
      setSending(false);
    }
  };

  const pickable = useMemo(
    () => debtors.map((d) => ({ id: d.playerId, name: d.playerName, ageGroup: d.ageGroup })),
    [debtors]
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[90vh] md:max-w-xl md:rounded-lg">
          <div className="flex shrink-0 items-center justify-between border-b p-4 md:px-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Fizetési emlékeztető</Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto p-4 pb-safe-6 md:px-6">
            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">Kinek?</h4>
              <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
                {([
                  ['all', 'Minden tartozó'],
                  ['ageGroups', 'Korosztályok'],
                  ['players', 'Kiválasztott játékosok'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setScope(value)}
                    aria-pressed={scope === value}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      scope === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {scope === 'ageGroups' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {ageGroupOptions.map((name) => {
                    const active = ageGroups.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setAgeGroups((prev) => (active ? prev.filter((x) => x !== name) : [...prev, name]))}
                        aria-pressed={active}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                          active ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {name} ({debtors.filter((d) => d.ageGroup === name).length})
                      </button>
                    );
                  })}
                </div>
              )}
              {scope === 'players' && (
                <div className="mt-3">
                  <PlayerPicker players={pickable} selectedIds={playerIds} onChange={setPlayerIds} placeholder="Keress a tartozók között..." />
                </div>
              )}
            </section>

            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">Hogyan?</h4>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={sendPush}
                    onChange={(e) => setSendPush(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Push értesítés
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Email
                </label>
              </div>
              {!sendEmail && !sendPush && <p className="mt-1 text-xs text-red-600">Válassz legalább egy módot.</p>}
            </section>

            <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              {targets.length > 0 ? (
                <>
                  <span className="font-semibold">{targets.length} tartozó</span> szülei (és sajátfiókos játékosai) kapják meg,
                  összesen {formatAmount(total)} tartozásról.
                </>
              ) : (
                'Még nincs kiválasztott tartozó.'
              )}
            </p>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 md:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              <BellAlertIcon className="h-4 w-4" />
              {sending ? 'Küldés...' : 'Küldés most'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default RemindFeesModal;
